import React, { useMemo } from 'react';
import { LoanData } from '../types/loan';
import { DollarSign, FileText, LockOpen, TrendingUp, AlertTriangle, ClipboardList } from 'lucide-react';
interface SummaryTilesProps {
  loans: LoanData[];
}
function parseCurrency(value: string): number {
  if (!value || value === '—' || value === 'Empty') return 0;
  return Number(value.replace(/[^0-9.-]/g, '')) || 0;
}
interface TileConfig {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  highlight?: 'warn' | 'success';
}
export function SummaryTiles({
  loans
}: SummaryTilesProps) {
  const stats = useMemo(() => {
    const volumes = loans.map(l => parseCurrency(l.loanAmount)).filter(v => v > 0);
    const totalVolume = volumes.reduce((a, b) => a + b, 0);
    const avgLoan = volumes.length > 0 ? Math.round(totalVolume / volumes.length) : 0;
    const notLocked = loans.filter(l => l.lockStatus === 'Not Locked').length;
    const openConditions = loans.reduce((sum, l) => {
      if (typeof l.remainingConditions === 'number') return sum + l.remainingConditions;
      return sum;
    }, 0);
    const cdsPending = loans.filter(l => l.cdStatus === 'Incomplete' || l.cdStatus === 'Sent').length;
    return {
      totalVolume,
      avgLoan,
      notLocked,
      openConditions,
      cdsPending
    };
  }, [loans]);
  const tiles: TileConfig[] = [{
    label: 'Total Volume',
    value: '$' + stats.totalVolume.toLocaleString('en-US'),
    icon: <DollarSign className="w-5 h-5 text-[#0f4496]" data-id="element-86" />,
    iconBg: 'bg-[#0f4496]/10'
  }, {
    label: 'Total Units',
    value: String(loans.length),
    icon: <FileText className="w-5 h-5 text-[#0f4496]" data-id="element-87" />,
    iconBg: 'bg-[#0f4496]/10'
  }, {
    label: 'Avg Loan Size',
    value: '$' + stats.avgLoan.toLocaleString('en-US'),
    icon: <TrendingUp className="w-5 h-5 text-[#0f4496]" data-id="element-88" />,
    iconBg: 'bg-[#0f4496]/10'
  }, {
    label: 'Not Locked',
    value: String(stats.notLocked),
    icon: <LockOpen className="w-5 h-5 text-amber-600" data-id="element-89" />,
    iconBg: 'bg-amber-50',
    highlight: stats.notLocked > 0 ? 'warn' : undefined
  }, {
    label: 'Open Conditions',
    value: String(stats.openConditions),
    icon: <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-90" />,
    iconBg: 'bg-amber-50',
    highlight: stats.openConditions > 0 ? 'warn' : undefined
  }, {
    label: 'CDs Pending',
    value: String(stats.cdsPending),
    icon: <ClipboardList className="w-5 h-5 text-sky-600" data-id="element-91" />,
    iconBg: 'bg-sky-50'
  }];
  return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-id="element-92">
      {tiles.map(tile => <div key={tile.label} className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] px-4 py-4" data-id="element-93">
          <div className={`w-10 h-10 rounded-xl ${tile.iconBg} flex items-center justify-center flex-shrink-0`} data-id="element-94">
            {tile.icon}
          </div>
          <div className="min-w-0" data-id="element-95">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider truncate" data-id="element-96">
              {tile.label}
            </p>
            <p className={`text-lg font-bold tabular-nums mt-0.5 ${tile.highlight === 'warn' ? 'text-amber-600' : 'text-slate-900'}`} data-id="element-97">
              {tile.value}
            </p>
          </div>
        </div>)}
    </div>;
}